/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: EmailConfigFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form field components for Email Config form.
────────────────────────────────────────────────────────────────*/

import { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { useI18n } from '../../../hooks/useI18n';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import { SecretInput } from '../../../components/ui/SecretField';
import { AUTH_TYPES, AUTH_CREDENTIALS_REQUIRED, type EmailConfigFormData } from '../hooks/useEmailConfigForm';

type FieldName = keyof EmailConfigFormData;

interface FormFieldsProps {
  register: UseFormRegister<EmailConfigFormData>;
  watch: UseFormWatch<EmailConfigFormData>;
  setValue: UseFormSetValue<EmailConfigFormData>;
  clearErrors: UseFormClearErrors<EmailConfigFormData>;
  setError: UseFormSetError<EmailConfigFormData>;
  errors: FieldErrors<EmailConfigFormData>;
}

export function useEmailConfigFormFields({
  register,
  watch,
  setValue,
  clearErrors,
  setError,
  errors,
}: FormFieldsProps) {
  const { t } = useI18n();
  const authType = watch('authType');

  const getFieldErrorMessage = (field: FieldName): string | undefined => {
    const fieldError = errors[field];
    if (!fieldError) return undefined;
    if (typeof fieldError?.message === 'string') return fieldError.message;
    return undefined;
  };

  const renderFieldError = (field: FieldName) => {
    const message = getFieldErrorMessage(field);
    if (!message) return null;
    return <p className="text-xs text-red-600">{message}</p>;
  };

  const renderLabel = (
    field: FieldName,
    {
      labelKey,
      tooltipKey,
      required,
    }: {
      labelKey: string;
      tooltipKey: string;
      required?: boolean;
    }
  ) => (
    <SimpleTooltip content={t(tooltipKey)} maxWidth="320px">
      <label htmlFor={field} className="text-sm font-medium text-foreground">
        {t(labelKey)}
        {required ? ' *' : ''}
      </label>
    </SimpleTooltip>
  );

  const renderInputField = (
    field: FieldName,
    {
      labelKey,
      tooltipKey,
      placeholderKey,
      required,
      type = 'text',
      registerOptions,
    }: {
      labelKey: string;
      tooltipKey: string;
      placeholderKey: string;
      required?: boolean;
      type?: string;
      registerOptions?: Parameters<typeof register>[1];
    }
  ) => {
    const hasError = Boolean(errors[field]);
    return (
      <>
        <div className="space-y-1">{renderLabel(field, { labelKey, tooltipKey, required })}</div>
        <div className="flex flex-col gap-1">
          <input
            id={field}
            type={type}
            placeholder={t(placeholderKey)}
            className={`w-full rounded border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-1 focus:ring-primary ${
              hasError ? 'border-red-500' : 'border-border'
            }`}
            aria-invalid={hasError}
            {...register(field, registerOptions)}
          />
          {renderFieldError(field)}
        </div>
      </>
    );
  };

  const renderSelectField = (
    field: FieldName,
    {
      labelKey,
      tooltipKey,
      options,
    }: {
      labelKey: string;
      tooltipKey: string;
      options: Array<{ value: string; labelKey: string }>;
    }
  ) => {
    const hasError = Boolean(errors[field]);
    return (
      <>
        <div className="space-y-1">{renderLabel(field, { labelKey, tooltipKey })}</div>
        <div className="flex flex-col gap-1">
          <select
            id={field}
            className={`w-full rounded border bg-background px-3 py-2 text-sm text-foreground focus:border-transparent focus:outline-none focus:ring-1 focus:ring-primary ${
              hasError ? 'border-red-500' : 'border-border'
            }`}
            aria-invalid={hasError}
            {...register(field)}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </select>
          {renderFieldError(field)}
        </div>
      </>
    );
  };

  const renderToggleField = (
    field: 'secure' | 'tls' | 'isActive' | 'isDefault',
    {
      labelKey,
      tooltipKey,
    }: {
      labelKey: string;
      tooltipKey: string;
    }
  ) => (
    <>
      <div className="space-y-1">{renderLabel(field, { labelKey, tooltipKey })}</div>
      <div className="flex justify-start">
        <Toggle
          value={watch(field)}
          onChange={(value: boolean) => setValue(field, value, { shouldDirty: true })}
        />
      </div>
    </>
  );

  const passwordValue = watch('password') ?? '';

  const renderPasswordField = () => {
    const hasError = Boolean(errors.password);
    return (
      <>
        <div className="space-y-1">
          {renderLabel('password', {
            labelKey: 'emailSettings.fields.password',
            tooltipKey: 'emailSettings.tooltips.password',
            required: AUTH_CREDENTIALS_REQUIRED.includes(authType),
          })}
        </div>
        <div className="flex flex-col gap-1">
          <SecretInput
            value={passwordValue}
            onChange={(value) => {
              setValue('password', value, { shouldDirty: true });
              if (errors.password) {
                clearErrors('password');
              }
            }}
            placeholder={t('emailSettings.placeholders.password')}
            className={hasError ? 'border-red-500' : ''}
            showCopy={false}
          />
          {renderFieldError('password')}
        </div>
      </>
    );
  };

  return {
    renderInputField,
    renderSelectField,
    renderToggleField,
    renderPasswordField,
  };
}

